import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { usePlayback } from '../context/playback';

var DEPTH_THRESHOLD = 1200;
var SCROLL_UP_THRESHOLD = 40;

var Pill = styled.button`
  position: fixed;
  /* Above the player bar (home indicator included) and moving with it. */
  bottom: ${function (p) {
    return p.$hasPlayer
      ? 'calc(72px + env(safe-area-inset-bottom, 0px))'
      : 'calc(16px + env(safe-area-inset-bottom, 0px))';
  }};
  left: 50%;
  transform: ${function (p) {
    return p.$hasPlayer
      ? 'translate3d(-50%, var(--visible-bottom-offset, 0px), 0)'
      : 'translateX(-50%)';
  }};
  display: flex;
  align-items: center;
  gap: 0.35em;
  background: black;
  color: white;
  border: none;
  border-radius: 20px;
  padding: 0.45em 1em 0.45em 0.8em;
  font-size: 0.75rem;
  font-weight: 700;
  font-family: inherit;
  cursor: pointer;
  z-index: 1000;
  opacity: ${function (p) {
    return p.$visible ? 0.85 : 0;
  }};
  visibility: ${function (p) {
    return p.$visible ? 'visible' : 'hidden';
  }};
  pointer-events: ${function (p) {
    return p.$visible ? 'auto' : 'none';
  }};
  transition:
    opacity 0.25s ease,
    visibility 0s linear ${function (p) {
        return p.$visible ? '0s' : '0.25s';
      }};
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.25);
  white-space: nowrap;

  &:hover {
    opacity: 1;
  }
`;

var ArrowUp = styled.span`
  display: inline-block;
  width: 0;
  height: 0;
  border-left: 4px solid transparent;
  border-right: 4px solid transparent;
  border-bottom: 5px solid white;
`;

function ScrollToTopButton() {
  var playback = usePlayback();
  var hasPlayer = !!playback.currentTrack;
  var state = useState(false);
  var visible = state[0];
  var setVisible = state[1];
  var lastScrollY = useRef(window.scrollY);
  var scrollUpDistance = useRef(0);
  var scrollingToTopRef = useRef(false);

  useEffect(function () {
    function onScroll() {
      var currentY = window.scrollY;

      // While a programmatic scroll-to-top is animating, ignore everything
      // until we arrive near the top
      if (scrollingToTopRef.current) {
        lastScrollY.current = currentY;
        if (currentY < 100) {
          scrollingToTopRef.current = false;
          scrollUpDistance.current = 0;
        }
        return;
      }

      var delta = lastScrollY.current - currentY;

      if (delta > 0) {
        // Scrolling up
        scrollUpDistance.current += delta;
      } else {
        // Scrolling down -- reset
        scrollUpDistance.current = 0;
      }

      lastScrollY.current = currentY;

      var deepEnough = currentY > DEPTH_THRESHOLD;
      var scrolledUpEnough = scrollUpDistance.current > SCROLL_UP_THRESHOLD;

      if (delta < 0) {
        // Scrolling down -- hide immediately
        setVisible(false);
      } else if (deepEnough && scrolledUpEnough) {
        setVisible(true);
      } else if (!deepEnough) {
        // Scrolled above the depth threshold -- hide
        setVisible(false);
      }
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    return function () {
      window.removeEventListener('scroll', onScroll);
    };
  }, []);

  function scrollToTop() {
    setVisible(false);
    scrollUpDistance.current = 0;
    scrollingToTopRef.current = true;
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  // On iOS Safari, tapping during momentum scroll stops the scroll but
  // does NOT fire onClick. Use onTouchEnd so the first tap works.
  function handleTouchEnd(e) {
    e.preventDefault();
    scrollToTop();
  }

  return (
    <Pill
      $visible={visible}
      $hasPlayer={hasPlayer}
      onClick={scrollToTop}
      onTouchEnd={handleTouchEnd}
      aria-label="Back to top"
    >
      <ArrowUp /> Back to top
    </Pill>
  );
}

export default ScrollToTopButton;
